import type { ColDef } from 'ag-grid-community'
import type { ColumnVisibilityOption } from 'src/hooks/useColumnVisibility'

/**
 * Columns the roster chooser never offers: the name is the row's identity and
 * the ⋯ menu is how a row is acted on — hiding either leaves a grid nobody can
 * read or use.
 */
export const ROSTER_LOCKED_COLUMN_IDS: readonly string[] = ['name', 'actions']

/**
 * The chooser's list, taken from the grid's own column defs so the two can
 * never disagree — a column that only exists sometimes (Signed Waiver) is
 * offered only when it is actually in the grid.
 */
export const chooserColumnsOf = <TData>(
  columns: ColDef<TData>[],
  lockedIds: readonly string[]
): ColumnVisibilityOption[] =>
  columns.flatMap((column) => {
    const { colId } = column
    if (!colId || lockedIds.includes(colId)) {
      return []
    }
    return [{ colId, headerName: column.headerName ?? colId }]
  })

/**
 * The grid's column defs with the chooser's choice applied as AG Grid's
 * `hide`. Locked columns stay visible whatever `isHidden` says.
 */
export const applyHiddenColumns = <TData>(
  columns: ColDef<TData>[],
  isHidden: (colId: string) => boolean
): ColDef<TData>[] =>
  columns.map((column) => {
    const { colId } = column
    return {
      ...column,
      hide:
        colId !== undefined &&
        !ROSTER_LOCKED_COLUMN_IDS.includes(colId) &&
        isHidden(colId),
    }
  })
